/**
 * Client-side IFC diff engine.
 *
 * Compares two loaded models by indexing every IfcRoot subtype that
 * carries a `GlobalId`, then walking the union to bucket entities
 * into added / removed / modified / unchanged. Runs entirely in the
 * browser against the existing parallel `web-ifc` parser
 * (`rawIfc`): no upload, no server round-trip.
 *
 * Scope (MVP):
 * - Element-level diff by GlobalId, restricted to a curated list of
 *   IfcRoot subtypes so the cost stays bounded on big federations.
 *   The list covers building elements (wall/slab/column/...),
 *   spatial structure, and the IfcProject root itself.
 * - Compares user-facing scalar attributes only: Name, Description,
 *   ObjectType, Tag, PredefinedType, plus a derived "Schema type"
 *   (IfcWall vs IfcSlab) so type changes are reported even when the
 *   GlobalId is preserved (rare but legal).
 * - Property sets and quantity sets (Psets/Qsets) ARE compared: each
 *   element's "PsetName.PropName -> value" map is diffed so changed,
 *   added or removed properties show up as fields on the modified row.
 * - Geometry, IfcLocalPlacement and IfcShapeRepresentation are still
 *   intentionally NOT compared: those need a deeper traversal that's
 *   better as a follow-up.
 *
 * Cost: extraction is O(N) over the matching entities of one model.
 * For typical models that's a few thousand IfcRoot rows; well under
 * a second on the parallel parser. Caller should still show a
 * spinner, especially the first time `rawIfc` warms up for a model.
 */
import { rawIfc } from './rawIfc'

/** IfcRoot subtypes we walk. Curated to cover what users atually
 * care about while staying away from millions of style/relationship
 * rows in big models. */
const COMPARABLE_TYPES: string[] = [
  'IFCPROJECT',
  'IFCSITE',
  'IFCBUILDING',
  'IFCBUILDINGSTOREY',
  'IFCSPACE',
  'IFCZONE',
  'IFCWALL',
  'IFCWALLSTANDARDCASE',
  'IFCSLAB',
  'IFCCOLUMN',
  'IFCBEAM',
  'IFCROOF',
  'IFCDOOR',
  'IFCWINDOW',
  'IFCSTAIR',
  'IFCSTAIRFLIGHT',
  'IFCRAILING',
  'IFCRAMP',
  'IFCRAMPFLIGHT',
  'IFCCOVERING',
  'IFCCURTAINWALL',
  'IFCPLATE',
  'IFCMEMBER',
  'IFCFURNITURE',
  'IFCFURNISHINGELEMENT',
  'IFCSANITARYTERMINAL',
  'IFCFLOWTERMINAL',
  'IFCPIPESEGMENT',
  'IFCDUCTSEGMENT',
  'IFCBUILDINGELEMENTPROXY',
  'IFCOPENINGELEMENT',
]

export interface EntitySnapshot {
  globalId: string
  expressID: number
  schemaType: string
  name: string | null
  description: string | null
  objectType: string | null
  tag: string | null
  predefinedType: string | null
  /** Name of the spatial container (IfcBuildingStorey / IfcSpace) the
   *  element sits in, so a "moved to another storey" shows up as a field. */
  container: string | null
  /** Flattened Pset/Qset values: "GroupName.PropName" -> stringified
   *  value. Compared key-by-key to surface property-level changes. */
  props: Map<string, string>
}

export interface IfcDiffEntry {
  globalId: string
  schemaType: string
  name: string | null
  /** "left.expressID,right.expressID" for modified rows; one side null
   * for added/removed. Lets the UI re-select the element later. */
  leftExpressID: number | null
  rightExpressID: number | null
  fields: { name: string; from: unknown; to: unknown }[]
}

export interface IfcDiffByType {
  schemaType: string
  added: number
  modified: number
  removed: number
  unchanged: number
}

export interface IfcDiff {
  leftModelId: string
  rightModelId: string
  summary: { added: number; modified: number; removed: number; unchanged: number }
  byType: IfcDiffByType[]
  added: IfcDiffEntry[]
  modified: IfcDiffEntry[]
  removed: IfcDiffEntry[]
  unchanged: IfcDiffEntry[]
}

function readScalar(v: unknown): string | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'object') {
    const o = v as { value?: unknown }
    if (o.value === null || o.value === undefined) return null
    return String(o.value)
  }
  return String(v)
}

// --- Pset/Qset extraction -------------------------------------------------
// getEntity flattens forward references but uses inverse=false, so an
// element's own `IsDefinedBy` is NOT populated. Instead we walk every
// IfcRelDefinesByProperties once (its RelatingPropertyDefinition + nested
// HasProperties / Quantities ARE forward refs, so flatten=true resolves
// them deeply) and fan the pset/qset values out to each RelatedObjects
// element. One pass per model, fully synchronous.

function unwrap(v: unknown): unknown {
  if (v && typeof v === 'object' && 'value' in (v as object)) {
    return (v as { value: unknown }).value
  }
  return v
}

function readStr(v: unknown): string {
  const u = unwrap(v)
  return u === null || u === undefined ? '' : String(u)
}

function readExpressId(ref: unknown): number | null {
  if (typeof ref === 'number') return ref
  if (ref && typeof ref === 'object') {
    const o = ref as { value?: unknown; expressID?: unknown }
    const cand = o.expressID ?? o.value
    if (typeof cand === 'number') return cand
  }
  return null
}

/** Read the value of an IfcProperty* / IfcQuantity* row as a string. */
function readPropValue(po: Record<string, unknown>): string {
  if (po.NominalValue !== undefined) return readStr(po.NominalValue)
  if (Array.isArray(po.EnumerationValues)) {
    return po.EnumerationValues.map(readStr).join(', ')
  }
  if (Array.isArray(po.ListValues)) {
    return po.ListValues.map(readStr).join(', ')
  }
  const q =
    po.LengthValue ??
    po.AreaValue ??
    po.VolumeValue ??
    po.CountValue ??
    po.WeightValue ??
    po.TimeValue
  if (q !== undefined) return readStr(q)
  return ''
}

/** Pull the rows ("Group.Prop" -> value) off one IfcPropertySet /
 *  IfcElementQuantity object. */
function psetRows(pset: Record<string, unknown>): Array<[string, string]> {
  const group = readStr(pset.Name) || '(sem nome)'
  const rows: Array<[string, string]> = []
  const collect = (arr: unknown) => {
    if (!Array.isArray(arr)) return
    for (const p of arr) {
      if (!p || typeof p !== 'object') continue
      const po = p as Record<string, unknown>
      const nm = readStr(po.Name)
      if (!nm) continue
      rows.push([`${group}.${nm}`, readPropValue(po)])
    }
  }
  collect(pset.HasProperties) // IfcPropertySet
  collect(pset.Quantities) // IfcElementQuantity
  return rows
}

/** Build elementExpressID -> ("Group.Prop" -> value) for a whole model in
 *  a single pass over IfcRelDefinesByProperties. */
function buildPsetIndex(modelId: string): Map<number, Map<string, string>> {
  const index = new Map<number, Map<string, string>>()
  let relIds: number[]
  try {
    relIds = rawIfc.getLineIDsByType(modelId, 'IFCRELDEFINESBYPROPERTIES')
  } catch {
    return index
  }
  for (const relId of relIds) {
    const rel = rawIfc.getEntity(modelId, relId) as Record<
      string,
      unknown
    > | null
    if (!rel) continue
    const pdef = rel.RelatingPropertyDefinition
    if (!pdef || typeof pdef !== 'object') continue
    const rows = psetRows(pdef as Record<string, unknown>)
    if (rows.length === 0) continue
    const related = rel.RelatedObjects
    if (!Array.isArray(related)) continue
    for (const ro of related) {
      const eid = readExpressId(ro)
      if (eid == null) continue
      let m = index.get(eid)
      if (!m) {
        m = new Map<string, string>()
        index.set(eid, m)
      }
      for (const [k, v] of rows) m.set(k, v)
    }
  }
  return index
}

/** Build elementExpressID -> spatial container name (storey / space) in a
 *  single pass over IfcRelContainedInSpatialStructure. One rel per storey,
 *  so this is cheap. Used to flag elements that changed storey. */
function buildContainerIndex(modelId: string): Map<number, string> {
  const index = new Map<number, string>()
  let relIds: number[]
  try {
    relIds = rawIfc.getLineIDsByType(
      modelId,
      'IFCRELCONTAINEDINSPATIALSTRUCTURE',
    )
  } catch {
    return index
  }
  for (const relId of relIds) {
    const rel = rawIfc.getEntity(modelId, relId) as Record<
      string,
      unknown
    > | null
    if (!rel) continue
    const structure = rel.RelatingStructure
    if (!structure || typeof structure !== 'object') continue
    const name = readStr((structure as Record<string, unknown>).Name)
    if (!name) continue
    const related = rel.RelatedElements
    if (!Array.isArray(related)) continue
    for (const ro of related) {
      const eid = readExpressId(ro)
      if (eid != null) index.set(eid, name)
    }
  }
  return index
}

/** Pull the curated attribute set off a flattened web-ifc entity. The
 *  prop map and container are filled in separately by indexModel. */
function entityToSnapshot(
  schemaType: string,
  expressID: number,
  entity: Record<string, unknown>,
): EntitySnapshot | null {
  const globalId = readScalar(entity.GlobalId)
  if (!globalId) return null
  return {
    globalId,
    expressID,
    schemaType,
    name: readScalar(entity.Name),
    description: readScalar(entity.Description),
    objectType: readScalar(entity.ObjectType),
    tag: readScalar(entity.Tag),
    predefinedType: readScalar(entity.PredefinedType),
    container: null,
    props: new Map<string, string>(),
  }
}

export interface IndexProgress {
  schemaType: string
  done: number
  total: number
}

/** Build a `globalId -> snapshot` index for one model. Yields
 * progress between types so the UI can show "a indexar IfcSlab…". */
export async function indexModel(
  modelId: string,
  onProgress?: (p: IndexProgress) => void,
  withProps = false,
): Promise<Map<string, EntitySnapshot>> {
  if (!rawIfc.hasModel(modelId)) {
    // Lazy load: cacheBytes was already called by useViewer at load
    // time, so we just need to fire the parallel parser.
    await rawIfc.loadModel(modelId)
  }
  // One-shot Pset/Qset index, only when property diffing is on.
  const propIndex = withProps ? buildPsetIndex(modelId) : null
  // Spatial container index is always built (one rel per storey, cheap).
  const containerIndex = buildContainerIndex(modelId)
  const out = new Map<string, EntitySnapshot>()
  for (let i = 0; i < COMPARABLE_TYPES.length; i++) {
    const schemaType = COMPARABLE_TYPES[i]
    onProgress?.({
      schemaType,
      done: i,
      total: COMPARABLE_TYPES.length,
    })
    const ids = rawIfc.getLineIDsByType(modelId, schemaType)
    for (const expressID of ids) {
      const e = rawIfc.getEntity(modelId, expressID) as
        | Record<string, unknown>
        | null
      if (!e) continue
      const snap = entityToSnapshot(schemaType, expressID, e)
      if (!snap) continue
      if (propIndex) {
        const pm = propIndex.get(expressID)
        if (pm) snap.props = pm
      }
      snap.container = containerIndex.get(expressID) ?? null
      out.set(snap.globalId, snap)
    }
  }
  onProgress?.({
    schemaType: '',
    done: COMPARABLE_TYPES.length,
    total: COMPARABLE_TYPES.length,
  })
  return out
}

const COMPARED_FIELDS: Array<keyof EntitySnapshot> = [
  'name',
  'description',
  'objectType',
  'tag',
  'predefinedType',
  'container',
]

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  description: 'Descrição',
  objectType: 'ObjectType',
  tag: 'Tag',
  predefinedType: 'PredefinedType',
  container: 'Contentor (piso)',
  schemaType: 'Tipo IFC',
}

/** Compute the diff between two pre-indexed models. Pure function. */
export function compareIndexes(
  leftModelId: string,
  rightModelId: string,
  left: Map<string, EntitySnapshot>,
  right: Map<string, EntitySnapshot>,
  /** Optional gid -> displacement (mm) for elements whose bbox centre
   *  moved between versions. Surfaced as a "Deslocamento" field so a
   *  moved-but-otherwise-identical element still counts as modified. */
  movedMm?: Map<string, number>,
): IfcDiff {
  const added: IfcDiffEntry[] = []
  const removed: IfcDiffEntry[] = []
  const modified: IfcDiffEntry[] = []
  const unchanged: IfcDiffEntry[] = []
  const byTypeMap = new Map<string, IfcDiffByType>()

  const ensureByType = (t: string): IfcDiffByType => {
    let row = byTypeMap.get(t)
    if (!row) {
      row = { schemaType: t, added: 0, modified: 0, removed: 0, unchanged: 0 }
      byTypeMap.set(t, row)
    }
    return row
  }

  for (const [gid, r] of right) {
    const l = left.get(gid)
    if (!l) {
      added.push({
        globalId: gid,
        schemaType: r.schemaType,
        name: r.name,
        leftExpressID: null,
        rightExpressID: r.expressID,
        fields: [],
      })
      ensureByType(r.schemaType).added += 1
      continue
    }
    const fields: { name: string; from: unknown; to: unknown }[] = []
    if (l.schemaType !== r.schemaType) {
      fields.push({
        name: 'schemaType',
        from: l.schemaType,
        to: r.schemaType,
      })
    }
    for (const k of COMPARED_FIELDS) {
      if (l[k] !== r[k]) {
        fields.push({ name: k, from: l[k], to: r[k] })
      }
    }
    // Property-level diff (Psets/Qsets). The `prop:` prefix lets the UI
    // and CSV label them distinctly from the scalar attributes above.
    if (l.props.size > 0 || r.props.size > 0) {
      const keys = new Set<string>()
      for (const k of l.props.keys()) keys.add(k)
      for (const k of r.props.keys()) keys.add(k)
      for (const k of keys) {
        const lv = l.props.get(k) ?? null
        const rv = r.props.get(k) ?? null
        if (lv !== rv) fields.push({ name: `prop:${k}`, from: lv, to: rv })
      }
    }
    // Position diff (bbox centre moved beyond the caller's threshold).
    const mm = movedMm?.get(gid)
    if (mm !== undefined && mm > 0) {
      fields.push({ name: 'pos', from: null, to: `${mm} mm` })
    }
    const entry: IfcDiffEntry = {
      globalId: gid,
      schemaType: r.schemaType,
      name: r.name,
      leftExpressID: l.expressID,
      rightExpressID: r.expressID,
      fields,
    }
    if (fields.length > 0) {
      modified.push(entry)
      ensureByType(r.schemaType).modified += 1
    } else {
      unchanged.push(entry)
      ensureByType(r.schemaType).unchanged += 1
    }
  }
  for (const [gid, l] of left) {
    if (right.has(gid)) continue
    removed.push({
      globalId: gid,
      schemaType: l.schemaType,
      name: l.name,
      leftExpressID: l.expressID,
      rightExpressID: null,
      fields: [],
    })
    ensureByType(l.schemaType).removed += 1
  }

  const byType = Array.from(byTypeMap.values()).sort((a, b) => {
    const ax = a.added + a.modified + a.removed
    const bx = b.added + b.modified + b.removed
    return bx - ax || a.schemaType.localeCompare(b.schemaType)
  })

  return {
    leftModelId,
    rightModelId,
    summary: {
      added: added.length,
      modified: modified.length,
      removed: removed.length,
      unchanged: unchanged.length,
    },
    byType,
    added,
    modified,
    removed,
    unchanged,
  }
}

/** Colours used to paint the diff in 3D and in the panel legend. Shared
 *  so the swatch always matches what's rendered on the model. */
export const DIFF_COLORS = {
  added: '#10b981', // emerald
  removed: '#ef4444', // red
  modified: '#f59e0b', // amber
} as const

/** Build the per-model (localId → hex) maps to paint a diff in 3D:
 *  removed elements are tinted on model A, added/modified on model B. */
export function buildDiffColorMap(
  diff: IfcDiff,
): Map<string, Map<number, string>> {
  const byModel = new Map<string, Map<number, string>>()
  const ensure = (id: string) => {
    let m = byModel.get(id)
    if (!m) {
      m = new Map<number, string>()
      byModel.set(id, m)
    }
    return m
  }
  const left = ensure(diff.leftModelId)
  const right = ensure(diff.rightModelId)
  for (const e of diff.removed) {
    if (e.leftExpressID != null) left.set(e.leftExpressID, DIFF_COLORS.removed)
  }
  for (const e of diff.added) {
    if (e.rightExpressID != null) right.set(e.rightExpressID, DIFF_COLORS.added)
  }
  for (const e of diff.modified) {
    if (e.rightExpressID != null)
      right.set(e.rightExpressID, DIFF_COLORS.modified)
  }
  return byModel
}

/** Flatten a diff into a CSV (one row per changed field, or one row per
 *  added/removed element). Uses ';' so European Excel opens it in columns
 *  out of the box; a UTF-8 BOM is added by the caller. */
export function diffToCsv(diff: IfcDiff): string {
  const esc = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const rows: string[] = [
    ['Estado', 'Tipo', 'Nome', 'GlobalId', 'Campo', 'De', 'Para']
      .map(esc)
      .join(';'),
  ]
  const push = (estado: string, e: IfcDiffEntry) => {
    const base = [estado, prettyType(e.schemaType), e.name ?? '', e.globalId]
    if (e.fields.length === 0) {
      rows.push([...base, '', '', ''].map(esc).join(';'))
    } else {
      for (const f of e.fields) {
        rows.push(
          [...base, fieldLabel(f.name), f.from ?? '', f.to ?? '']
            .map(esc)
            .join(';'),
        )
      }
    }
  }
  for (const e of diff.added) push('Novo', e)
  for (const e of diff.modified) push('Alterado', e)
  for (const e of diff.removed) push('Removido', e)
  return rows.join('\r\n')
}

export function fieldLabel(name: string): string {
  // Pset/Qset rows carry a `prop:` prefix + "Group.Prop" key; show the
  // key as-is (it's already self-describing, e.g. Pset_WallCommon.IsExternal).
  if (name.startsWith('prop:')) return name.slice(5)
  if (name === 'pos') return 'Deslocamento'
  return FIELD_LABELS[name] ?? name
}

export function prettyType(schemaType: string): string {
  // IFCWALLSTANDARDCASE -> IfcWallStandardCase
  if (!schemaType) return schemaType
  const lower = schemaType.toLowerCase()
  if (!lower.startsWith('ifc')) return schemaType
  const rest = lower.slice(3)
  return 'Ifc' + rest.charAt(0).toUpperCase() + rest.slice(1)
}
